import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import {
  type AppLocale,
  clearLocaleSyncPending,
  DEFAULT_LOCALE,
  isAppLocale,
  markLocaleSyncPending,
} from './config';

/** Issues the `preferredLanguage` write-through, clearing the pending-sync marker on confirmed
 * success and leaving it set (logged) on failure — never a silently swallowed rejection
 * (review-gate B3). Shared by `setLocale`'s own write and `OperatorProvider`'s retry-on-next-load
 * path, mirroring `ThemeProvider.tsx`'s own `syncThemeToBackend` helper. */
export async function syncLocaleToBackend(locale: AppLocale): Promise<void> {
  try {
    const { error, response } = await apiClient.PATCH('/v1/profile', {
      body: { preferredLanguage: locale },
    });
    if (error !== undefined || !response.ok) {
      console.error('Failed to sync locale preference to the backend.', error);
      return;
    }
    // code-review (2026-07-15, F4): pass `locale` (this call's own target) so an older,
    // overlapping write cannot clear a marker a newer write has since overwritten with its own
    // target — see `clearLocaleSyncPending`'s doc comment in `config.ts`.
    clearLocaleSyncPending(locale);
  } catch (error) {
    console.error('Failed to sync locale preference to the backend.', error);
  }
}

/**
 * The active app locale plus a setter (SL-0027, Task 7.3). `setLocale` calls
 * `i18n.changeLanguage(next)` — `i18next-browser-languagedetector`'s own `detection.caches`
 * setting (Task 7.2) already writes the resolved language back to `LOCALE_STORAGE_KEY` as a side
 * effect of that call, so no separate explicit persistence call is needed for the local value.
 * Per Resolution 17/Task 12.3, `setLocale` also write-throughs to `PATCH /v1/profile`, guarded by
 * the same persisted pending-sync marker design as `ThemeProvider.tsx`'s `setMode` (review-gate
 * B3): the marker is set before the PATCH and cleared only on confirmed success, so a failed sync
 * is retried on the next `OperatorProvider` mount rather than silently lost, and never overwritten
 * by a stale backend hydration in the meantime.
 */
export function useLocale(): { locale: AppLocale; setLocale: (next: AppLocale) => void } {
  const { i18n } = useTranslation();
  const resolved = i18n.resolvedLanguage;
  const locale: AppLocale = isAppLocale(resolved) ? resolved : DEFAULT_LOCALE;

  const setLocale = (next: AppLocale): void => {
    void i18n.changeLanguage(next);
    markLocaleSyncPending(next);
    void syncLocaleToBackend(next);
  };

  return { locale, setLocale };
}
